import { formatEscPosQtyLine, formatItemsQtySummary } from './soldBy';
import {
  calculateBillSavings,
  formatDiscountLabel,
  formatDiscountPercent,
  formatItemSavingsText,
  getItemMrp,
  getItemSavings,
  payLaterCashierNotes,
  payLaterHeadline,
  payLaterLedger,
  payLaterMethodLines,
  receiptTenderLines,
  TENDER_LABEL,
} from './posMoney';

export const POS_STORE_NAME = 'BHIMA';
export const POS_STORE_ADDRESS = 'Praneeth Pranav Jaitra, ClubHouse Ground floor';
export const POS_GSTIN = '36AYRPG7411N2ZV';
export const POS_COUNTER = 'POS1';

/** True when the receipt/sale is a same-day void (History Delete). */
export function isPosReceiptVoided(receipt) {
  if (!receipt) return false;
  if (receipt.voided === true) return true;
  if (String(receipt.status || '').toUpperCase() === 'VOIDED') return true;
  if (String(receipt.payment_status || '').toUpperCase() === 'VOIDED') return true;
  return String(receipt.note || '').toUpperCase().startsWith('VOIDED');
}

/** True when the receipt is an amended / revised POS bill (not a void). */
export function isPosReceiptRevised(receipt) {
  if (!receipt || isPosReceiptVoided(receipt)) return false;
  if (receipt.revised === true) return true;
  const revision = Number(receipt.receipt_revision);
  return Number.isFinite(revision) && revision > 1;
}

export function posReceiptRevisionLabel(receipt) {
  const revision = Number(receipt?.receipt_revision);
  if (Number.isFinite(revision) && revision > 0) return String(revision);
  return isPosReceiptRevised(receipt) ? '2' : null;
}

const LINE_WIDTH = 32;
const PAYMENT_LABEL = TENDER_LABEL;

function cmd(...bytes) {
  return Uint8Array.from(bytes);
}

function text(str) {
  const safe = String(str)
    .replace(/₹/g, 'Rs')
    .replace(/[^\x20-\x7E\n\r\t]/g, '?');
  return new TextEncoder().encode(safe);
}

function concatBytes(...parts) {
  const arrays = parts.map((part) => (part instanceof Uint8Array ? part : Uint8Array.from(part)));
  const total = arrays.reduce((sum, arr) => sum + arr.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const arr of arrays) {
    out.set(arr, offset);
    offset += arr.length;
  }
  return out;
}

function formatMoney(amount) {
  const value = Number(amount) || 0;
  return `Rs ${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function wrapText(value, maxLen = LINE_WIDTH) {
  const words = String(value).trim().split(/\s+/);
  const lines = [];
  let current = '';

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxLen) {
      current = next;
      return;
    }
    if (current) lines.push(current);
    if (word.length > maxLen) {
      for (let i = 0; i < word.length; i += maxLen) {
        lines.push(word.slice(i, i + maxLen));
      }
      current = '';
      return;
    }
    current = word;
  });

  if (current) lines.push(current);
  return lines.length ? lines : [''];
}

function padLine(left, right, width = LINE_WIDTH) {
  const leftText = String(left);
  const rightText = String(right);
  const gap = Math.max(1, width - leftText.length - rightText.length);
  return `${leftText}${' '.repeat(gap)}${rightText}`;
}

function twoColLine(left, right, width = LINE_WIDTH) {
  const leftText = String(left);
  const rightText = String(right);
  if (leftText.length + rightText.length + 1 >= width) {
    return `${leftText}\n${rightText}`;
  }
  return padLine(leftText, rightText, width);
}

function formatReceiptDate(dt) {
  const day = String(dt.getDate()).padStart(2, '0');
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const year = String(dt.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

function formatReceiptTime(dt) {
  return dt.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function divider(char = '-') {
  return text(`${char.repeat(LINE_WIDTH)}\n`);
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function encodeRasterLogo(img, maxWidth = 384) {
  const scale = Math.min(1, maxWidth / img.naturalWidth);
  const width = Math.max(1, Math.floor(img.naturalWidth * scale));
  const height = Math.max(1, Math.floor(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  const { data } = ctx.getImageData(0, 0, width, height);

  const bytesPerRow = Math.ceil(width / 8);
  const raster = new Uint8Array(bytesPerRow * height);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      const lum = (data[i] * 0.299) + (data[i + 1] * 0.587) + (data[i + 2] * 0.114);
      const isDark = data[i + 3] > 128 && lum < 190;
      if (isDark) {
        const byteIndex = (y * bytesPerRow) + (x >> 3);
        raster[byteIndex] |= (0x80 >> (x & 7));
      }
    }
  }

  return concatBytes(
    cmd(0x1D, 0x76, 0x30, 0x00, bytesPerRow & 0xff, (bytesPerRow >> 8) & 0xff, height & 0xff, (height >> 8) & 0xff),
    raster,
    text('\n'),
  );
}

async function encodeLogoHeader(logoSrc, storeName) {
  if (!logoSrc) {
    return concatBytes(cmd(0x1B, 0x45, 0x01), text(`${storeName}\n`), cmd(0x1B, 0x45, 0x00));
  }
  try {
    const img = await loadImage(logoSrc);
    return encodeRasterLogo(img);
  } catch {
    return concatBytes(cmd(0x1B, 0x45, 0x01), text(`${storeName}\n`), cmd(0x1B, 0x45, 0x00));
  }
}

/**
 * Build ESC/POS bytes for a 76mm (3") thermal roll from API receipt JSON.
 */
export async function encodeReceipt(receipt, {
  changeDue = 0,
  storeName = POS_STORE_NAME,
  storeAddress = POS_STORE_ADDRESS,
  logoSrc,
  products = [],
} = {}) {
  const chunks = [];
  const dt = new Date(receipt.created_at);
  const dateShort = formatReceiptDate(dt);
  const timeShort = formatReceiptTime(dt);
  const taxedSlabs = (receipt.gst_breakup || []).filter((row) => row.rate > 0);

  const counter = receipt.counter || POS_COUNTER;
  const gstin = receipt.gstin || POS_GSTIN;
  const cashier = receipt.cashier_name || '—';
  const customerNumber = receipt.customer_number || '—';

  chunks.push(cmd(0x1B, 0x40)); // ESC @ init
  chunks.push(cmd(0x1B, 0x61, 0x01)); // center
  chunks.push(await encodeLogoHeader(logoSrc, storeName));
  chunks.push(text(`${storeAddress}\n`));
  chunks.push(text(`GSTIN: ${gstin}\n`));
  chunks.push(cmd(0x1B, 0x45, 0x01));
  if (isPosReceiptVoided(receipt)) {
    chunks.push(text('VOID\n'));
    chunks.push(cmd(0x1B, 0x45, 0x00));
    chunks.push(text('Bill\n'));
  } else if (isPosReceiptRevised(receipt)) {
    chunks.push(text('REVISED\n'));
    chunks.push(cmd(0x1B, 0x45, 0x00));
    const revision = posReceiptRevisionLabel(receipt);
    if (revision) chunks.push(text(`Revision ${revision}\n`));
    chunks.push(text('Bill\n'));
  } else {
    chunks.push(text('Bill\n'));
    chunks.push(cmd(0x1B, 0x45, 0x00));
  }
  chunks.push(divider());
  chunks.push(cmd(0x1B, 0x61, 0x00)); // left
  chunks.push(text(`${twoColLine(`Counter:${counter}`, `Bill.no:${receipt.display_id || '—'}`)}\n`));
  if (isPosReceiptRevised(receipt)) {
    const revision = posReceiptRevisionLabel(receipt);
    if (revision) chunks.push(text(`${padLine('Rev:', revision)}\n`));
  }
  if (isPosReceiptVoided(receipt) && receipt.void_reason) {
    chunks.push(text(`${padLine('Void reason:', receipt.void_reason)}\n`));
  }
  chunks.push(text(`${twoColLine(`Date:${dateShort} ${timeShort}`, `User:${cashier}`)}\n`));
  chunks.push(divider());
  chunks.push(text(`${padLine('Customer No:', customerNumber)}\n`));
  if (receipt.customer_name) {
    chunks.push(text(`${padLine('Name:', receipt.customer_name)}\n`));
  }
  if (receipt.customer_phone || receipt.phone) {
    chunks.push(text(`${padLine('Phone:', receipt.customer_phone || receipt.phone)}\n`));
  }
  if (String(receipt.payment_method || '').toUpperCase() === 'PAY_LATER') {
    const addressLines = [
      receipt.address_line1 ? `Address: ${receipt.address_line1}` : null,
      receipt.address_line2 || null,
      [receipt.block ? `Blk ${receipt.block}` : null, receipt.flat ? `Flat ${receipt.flat}` : null]
        .filter(Boolean)
        .join(', ') || null,
    ].filter(Boolean);
    addressLines.forEach((line) => {
      wrapText(line).forEach((wrapped) => {
        chunks.push(text(`${wrapped}\n`));
      });
    });
  }
  chunks.push(divider());

  (receipt.line_items || []).forEach((item) => {
    wrapText(item.name).forEach((line) => {
      chunks.push(text(`${line}\n`));
    });
    chunks.push(text(formatEscPosQtyLine(item, formatMoney)));

    const mrp = getItemMrp(item, products);
    const savings = getItemSavings(item, mrp);
    if (savings.unitSavings > 0) {
      const itemSaveText = `  ${formatItemSavingsText(savings, item, formatMoney, { ascii: true })}`;
      wrapText(itemSaveText).forEach((line) => {
        chunks.push(text(`${line}\n`));
      });
    }
    chunks.push(text('\n'));
  });

  chunks.push(divider());

  const lineItems = receipt.line_items || [];
  chunks.push(text(`${padLine('Total Items/Qty', formatItemsQtySummary(lineItems))}\n`));
  chunks.push(text(`${padLine('Subtotal', formatMoney(receipt.subtotal))}\n`));

  if ((receipt.discount_amount ?? 0) > 0) {
    const label = formatDiscountLabel(receipt, { ascii: true });
    const amountText = `-${formatMoney(receipt.discount_amount)}`;
    if (label.length + amountText.length + 1 >= LINE_WIDTH) {
      chunks.push(text(`${label}\n`));
      chunks.push(text(`${padLine('', amountText)}\n`));
    } else {
      chunks.push(text(`${padLine(label, amountText)}\n`));
    }
  }

  const roundOff = Number(receipt.round_off) || 0;
  if (roundOff !== 0) {
    const signed = roundOff > 0
      ? `+${formatMoney(roundOff)}`
      : formatMoney(roundOff);
    chunks.push(text(`${padLine('Round off', signed)}\n`));
  }

  chunks.push(cmd(0x1B, 0x45, 0x01));
  chunks.push(text(`${padLine('To pay', formatMoney(receipt.total))}\n`));
  chunks.push(cmd(0x1B, 0x45, 0x00));

  const billSavings = calculateBillSavings(receipt, products);
  if (billSavings.totalSavings > 0) {
    const pctStr = billSavings.totalSavingsPct > 0 ? ` (${formatDiscountPercent(billSavings.totalSavingsPct)})` : '';
    const savingsValue = `${formatMoney(billSavings.totalSavings)}${pctStr}`;
    chunks.push(text(`${twoColLine('Total Savings', savingsValue)}\n`));
  }

  const gstNote = !receipt.note || /^PAY LATER\b/i.test(String(receipt.note))
    ? '(Inclusive of GST)'
    : receipt.note;
  chunks.push(text(`${gstNote}\n`));

  if (taxedSlabs.length > 0) {
    chunks.push(divider());
    chunks.push(text(`${padLine('GST', 'CGST / SGST')}\n`));
    taxedSlabs.forEach((row) => {
      chunks.push(text(`${row.rate}% on ${formatMoney(row.taxable)}\n`));
      chunks.push(text(`${padLine('', `${formatMoney(row.cgst)} / ${formatMoney(row.sgst)}`)}\n`));
    });
  }

  chunks.push(divider());
  {
    const method = String(receipt.payment_method || '').toUpperCase();
    if (method === 'PAY_LATER') {
      const ledger = payLaterLedger(receipt);
      const headline = payLaterHeadline(receipt, formatMoney);
      wrapText(headline).forEach((line) => {
        chunks.push(text(`${line}\n`));
      });
      const methodLines = payLaterMethodLines(ledger.paidBy);
      if (methodLines.length > 1) {
        methodLines.forEach((line) => {
          chunks.push(text(`${padLine(line.label, formatMoney(line.amount))}\n`));
        });
      } else if (ledger.amountPaid > 0) {
        const via = PAYMENT_LABEL[ledger.lastMethod] || '';
        const paidLabel = ledger.collected ? 'Paid' : 'Paid now';
        const paidAmount = ledger.collected ? ledger.amountPaid : (ledger.lastAmount || ledger.amountPaid);
        chunks.push(text(`${padLine(via ? `${paidLabel} ${via}` : paidLabel, formatMoney(paidAmount))}\n`));
      }
      if (!ledger.collected) {
        chunks.push(text(`${padLine('Amount due', formatMoney(ledger.outstanding))}\n`));
      }
      if (ledger.lastRoundOff !== 0) {
        chunks.push(text(`${padLine('Cash round', formatMoney(ledger.lastRoundOff))}\n`));
      }
      payLaterCashierNotes(receipt).forEach((note) => {
        wrapText(`Note: ${note}`).forEach((line) => {
          chunks.push(text(`${line}\n`));
        });
      });
    } else {
      const tenderLines = receiptTenderLines(receipt);
      if (tenderLines.length > 1) {
        tenderLines.forEach((line) => {
          const label = PAYMENT_LABEL[line.method] || line.method;
          chunks.push(text(`${padLine(`Paid via ${label}`, formatMoney(line.amount))}\n`));
        });
      } else {
        chunks.push(text(`${padLine('Paid via', PAYMENT_LABEL[receipt.payment_method] || receipt.payment_method)}\n`));
        chunks.push(text(`${padLine('Amount', formatMoney(receipt.total))}\n`));
      }
    }
  }
  if (changeDue > 0) {
    chunks.push(text(`${padLine('Change', formatMoney(changeDue))}\n`));
  }

  if (billSavings.totalSavings > 0) {
    chunks.push(cmd(0x1B, 0x61, 0x01)); // center
    chunks.push(text(`*** Total Savings: ${formatMoney(billSavings.totalSavings)} ***\n`));
    chunks.push(cmd(0x1B, 0x61, 0x00)); // left
  }

  chunks.push(cmd(0x1B, 0x61, 0x01)); // center
  chunks.push(text('\nGive Us feedback on bhimasupermarkets@svyminds.com\n'));
  chunks.push(text('Thank you for shopping with us!\n'));
  chunks.push(text('\n\n\n'));
  chunks.push(cmd(0x1D, 0x56, 0x01)); // partial cut

  return concatBytes(...chunks);
}
